/**
 * Session windows for the tape.
 *
 * The store holds months of prints and the endpoint defaults to the last 24 hours, so "what is
 * trading" and "what traded this week" are very different questions against the same table — and
 * the answer changes character, not just size: recent days carry a handful of symbols in enormous
 * depth, while earlier days carry thousands of symbols. Making the window an explicit, first-class
 * control is therefore the difference between a board that looks broken and one that does not.
 *
 * Boundaries are UTC, matching how the tape stores timestamps; no local-time guessing.
 */

export type TimeWindowId = 'last_session' | 'today' | 'yesterday' | 'd3' | 'week' | 'month' | 'all';

export interface TimeWindowOption {
  id: TimeWindowId;
  label: string;
  /** Days back from the start of today; null means no lower bound. */
  days: number | null;
  /** Yesterday is the one window with an upper bound too. */
  endsYesterday?: boolean;
}

export const TIME_WINDOWS: TimeWindowOption[] = [
  { id: 'last_session', label: 'Last session', days: null },
  { id: 'today', label: 'Today', days: 0 },
  { id: 'yesterday', label: 'Yesterday', days: 1, endsYesterday: true },
  { id: 'd3', label: '3 days', days: 3 },
  { id: 'week', label: 'Week', days: 7 },
  { id: 'month', label: 'Month', days: 30 },
  { id: 'all', label: 'All', days: null },
];

/** US cash open in UTC. Before it, "today" holds nothing worth showing. */
const US_OPEN_UTC_HOUR = 13.5;

/**
 * The most recent day that actually traded: weekends step back to Friday, and a weekday morning
 * before the open steps back to the previous session. This is what makes the board useful at a
 * weekend — the off-hours feed carries a handful of symbols, while the last session carries
 * thousands.
 */
export function lastSessionStart(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setUTCHours(0, 0, 0, 0);
  const nowHours = now.getUTCHours() + now.getUTCMinutes() / 60;
  // Today counts only once the cash session has opened.
  if (now.getUTCDate() === d.getUTCDate() && nowHours < US_OPEN_UTC_HOUR) {
    d.setUTCDate(d.getUTCDate() - 1);
  }
  // 0 = Sunday, 6 = Saturday.
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) {
    d.setUTCDate(d.getUTCDate() - 1);
  }
  return d;
}

function startOfUtcDay(offsetDays: number): Date {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - offsetDays);
  return d;
}

export interface ResolvedWindow {
  fromTs?: string;
  toTs?: string;
}

/**
 * The lower bound sent for `all`.
 *
 * An explicit bound, not an absent one. The tape endpoint applies its own 24-hour default
 * whenever a request carries no bounds at all, so returning `{}` here made "All" quietly mean
 * "last 24 hours" -- and therefore return *less* than "Week", which is the one thing it can
 * never legitimately do. Any date earlier than the retention window works; this one is
 * unmistakably a sentinel.
 */
const ALL_HISTORY_FROM = '1970-01-01T00:00:00.000Z';

/** Absolute UTC bounds for a window id. `all` reaches back past anything retained. */
export function resolveTimeWindow(id: TimeWindowId, now: Date = new Date()): ResolvedWindow {
  if (id === 'last_session') {
    const from = lastSessionStart(now);
    const to = new Date(from);
    to.setUTCDate(to.getUTCDate() + 1);
    return { fromTs: from.toISOString(), toTs: to.toISOString() };
  }
  if (id === 'all') return { fromTs: ALL_HISTORY_FROM };
  const opt = TIME_WINDOWS.find((w) => w.id === id);
  if (!opt || opt.days == null) return {};
  const from = startOfUtcDay(opt.days);
  const out: ResolvedWindow = { fromTs: from.toISOString() };
  if (opt.endsYesterday) out.toTs = startOfUtcDay(opt.days - 1).toISOString();
  return out;
}

/** Human summary for the header, e.g. "Week · from 2026-08-09". */
export function describeWindow(id: TimeWindowId): string {
  if (id === 'last_session') {
    return `Last session · ${lastSessionStart().toISOString().slice(0, 10)}`;
  }
  const opt = TIME_WINDOWS.find((w) => w.id === id);
  if (!opt) return '';
  // "All history" overstated it: the tape is retained for days, not forever, so this is
  // everything the store still holds rather than everything that ever traded.
  if (opt.days == null) return 'All retained history';
  const { fromTs, toTs } = resolveTimeWindow(id);
  const from = (fromTs || '').slice(0, 10);
  return toTs ? `${opt.label} · ${from}` : `${opt.label} · from ${from}`;
}
